import {
   type Money,
   type ProductVariantID,
   filterNullableItems,
   getVariantIdFromVariantURI,
   parseItemcode,
} from '@ifixit/helpers';
import { useTranslations } from '@ifixit/i18n';
import { type CartLineItem } from '@ifixit/shopify-cart-sdk';
import { useCart } from '@ifixit/shopify-cart-sdk/hooks';
import {
   getCategoriesFromAnalyticsDimensions,
   parseAnalyticsDimensionsMetafield,
   parseOemPartnershipMetafield,
} from '@ifixit/shopify-cart-sdk/models/cart';
import {
   type FindMerchProductQuery,
   useShopifyStorefrontClient,
} from '@ifixit/shopify-storefront-client';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { MerchProduct, MerchProductVariant } from '../types';
import { useGetUserPrice } from '../../commerce';

const ALL_MERCH = [
   {
      handle: 'orbit-t-shirt',
      productcode: '179105',
      imageSrc: 'https://assets.cdn.ifixit.com/static/images/cart/product/cart-orbit-tshirt.png',
      i18nHeader: 'addAnOrbitTShirt',
      i18nSubtext: 'orbitSubtext',
   },
   {
      handle: 'exploded-switch-t-shirt',
      productcode: '179107',
      imageSrc:
         'https://assets.cdn.ifixit.com/static/images/cart/product/cart-switch-tshirt-back.png',
      i18nHeader: 'addAnExplodedSwitchTShirt',
      i18nSubtext: 'explodedSwitchSubtext',
   },
] as const;

export function useMerchProduct(): MerchProduct | null {
   const { client, storeCode } = useShopifyStorefrontClient();
   const getUserPrice = useGetUserPrice({ storeCode });
   const t = useTranslations('components.MerchBanner');
   const memoizedMerch = useMemoizedMerch();

   const { data: merchProduct } = useQuery({
      queryKey: ['find-merch-product', storeCode, memoizedMerch?.handle ?? 'no-merch-handle'],
      queryFn: async (): Promise<MerchProduct | null> => {
         if (memoizedMerch == null) {
            return null;
         }
         const queryResult = await client.findMerchProduct({ handle: memoizedMerch.handle });
         return createMerchProduct(queryResult);
      },
   });
   return merchProduct ?? null;

   function createMerchProduct(queryResult: FindMerchProductQuery): MerchProduct | null {
      const product = queryResult.product;
      if (product == null || memoizedMerch == null) {
         return null;
      }
      const variants = product.variants.nodes;
      const merchProduct: MerchProduct = {
         imageSrc: memoizedMerch.imageSrc,
         handle: product.handle,
         variants: filterNullableItems(
            variants.map(apiVariant => createMerchProductVariant({ product, apiVariant }))
         ),
         title: product.title,
         header: t(memoizedMerch.i18nHeader),
         subtext: t(memoizedMerch.i18nSubtext),
      };
      if (merchProduct.variants.length === 0) {
         return null;
      }
      return merchProduct;
   }

   type ApiVariant = NonNullable<FindMerchProductQuery['product']>['variants']['nodes'][0];
   function createMerchProductVariant({
      apiVariant,
      product,
   }: {
      apiVariant: ApiVariant;
      product: NonNullable<FindMerchProductQuery['product']>;
   }): MerchProductVariant | null {
      const enabled = apiVariant.enabled?.value === 'true';
      const inStock = apiVariant.quantityAvailable && apiVariant.quantityAvailable > 0;
      const apparelSize = apiVariant.selectedOptions.find(
         options => options.name === 'Apparel Size'
      );
      if (!enabled || !inStock || !apiVariant.id || !apiVariant.sku || !apparelSize) {
         return null;
      }
      const globalVariantId = apiVariant.id as ProductVariantID;
      const variantid = getVariantIdFromVariantURI(globalVariantId);
      const proPricesByTier = parsePriceTiers(
         apiVariant.proPricesByTier,
         apiVariant.price.currencyCode
      );
      const userPrice = getUserPrice({
         price: apiVariant.price,
         compareAtPrice: apiVariant.compareAtPrice,
         proPricesByTier,
      });
      const analyticsDimensions = parseAnalyticsDimensionsMetafield(apiVariant.analyticsDimensions);
      const oemPartnership = parseOemPartnershipMetafield(product.oemPartnership);
      const lineItem: CartLineItem = {
         name: product.title,
         variantTitle: apiVariant.title,
         itemcode: apiVariant.sku,
         shopifyVariantId: globalVariantId,
         quantity: 1,
         imageSrc: apiVariant.image?.url,
         price: userPrice.price,
         compareAtPrice: userPrice.compareAtPrice,
         categories: getCategoriesFromAnalyticsDimensions(analyticsDimensions),
         url: `/products/${product.handle}?variant=${variantid}`,
         fulfiller: analyticsDimensions?.fulfiller,
         oemPartnership: oemPartnership,
      };
      const variant: MerchProductVariant = {
         apparelSize: apparelSize.value,
         lineItem,
         proPricesByTier,
      };
      return variant;
   }
}

function useMemoizedMerch() {
   const { data: cart } = useCart();
   const cartProdutcodes = cart
      ? cart.lineItems.map(line => parseItemcode(line.itemcode).productcode)
      : [];
   const merchNotInCart = ALL_MERCH.filter(merch => !cartProdutcodes.includes(merch.productcode));
   merchNotInCart.sort();
   return useMemo(
      () =>
         merchNotInCart.length > 0
            ? merchNotInCart[Math.floor(Math.random() * merchNotInCart.length)]
            : null,
      [JSON.stringify(merchNotInCart)]
   );
}

function parsePriceTiers(
   priceTiersMetafield: { value: string } | null | undefined,
   currencyCode: string
): Record<string, Money> {
   let priceTiers: Record<string, number>;
   try {
      priceTiers = JSON.parse(priceTiersMetafield?.value ?? '');
   } catch {
      return {};
   }
   return Object.entries(priceTiers).reduce(
      (acc, [key, value]) => {
         acc[key] = {
            amount: value,
            currencyCode: currencyCode,
         };
         return acc;
      },
      {} as Record<string, Money>
   );
}
